import type { HistoryValue } from "./shipment-history";

// Current-read DTO: Server 07e9d155a5b1df0ac971e1f6ae3acb9d083d54fa.
// Display evidence never substitutes for current operational values.
export type RetainedDisplay<T> =
  | { availability: "RETAINED_DISPLAY"; value: T }
  | { availability: "UNKNOWN_LEGACY"; value: null };
export type ServiceDisplay = {
  id: string;
  code: string | null;
  name: string | null;
};
export type PackageTypeDisplay = {
  id: string;
  name: string;
  lengthCm: string;
  widthCm: string;
  heightCm: string;
  weightLimit: number | null;
};
export type CurrentPhysicalAddress = {
  id?: string;
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  province?: string | null;
  country?: string | null;
  postalCode?: string | null;
  plusCode?: string | null;
  location?: { lat: number; lng: number } | null;
};
export type CurrentFulfillmentPlan = {
  shipmentId: string;
  revision: number;
  inductionKind: string;
  facilityDetailsVersion: number | null;
  dispatchName: string | null;
  dispatchCode: string | null;
  dispatchContactName: string | null;
  dispatchContactPhone: string | null;
  dispatchContactEmail: string | null;
  carrierDropoffName: string | null;
  dispatchCenterId: string | null;
  carrierDropoffLocationId: string | null;
  dispatchAddressId: string | null;
  carrierDropoffAddressId: string | null;
  dispatchAddress: CurrentPhysicalAddress | null;
  carrierDropoffAddress: CurrentPhysicalAddress | null;
  locationConfigVersion: number | null;
  dispatchProcessingMinutes: number | null;
  dispatchRunSchedule: HistoryValue;
  carrierDropoffOperatingHours: HistoryValue;
  carrierDropoffTimeZone: string | null;
  timeZone: string;
  pickupDate: string | null;
  assumedHandoffAt: string | null;
  readyAt: string | null;
  handoffBy: string | null;
  atCarrierFastest: string | null;
  deliveredFastest: string;
  deliveredSlowest: string;
  deliveredPrecision: "DATE" | "DATETIME";
  acceptedAt: string;
};
export interface ShipmentCurrentExtras {
  carrierServiceId?: string | null;
  selectedService?: RetainedDisplay<ServiceDisplay>;
  options?: { signatureRequired?: boolean; insurance?: boolean } | null;
  fulfillmentPlan?: CurrentFulfillmentPlan | null;
}
export interface PieceCurrentExtras {
  packageKind?: string | null;
  packageTypeId?: string | null;
  weightOriginal?: string | number | null;
  weightOriginalUnit?: string | null;
  lengthOriginal?: string | number | null;
  widthOriginal?: string | number | null;
  heightOriginal?: string | number | null;
  dimensionOriginalUnit?: string | null;
  packageTypeDisplay?: RetainedDisplay<PackageTypeDisplay>;
  declaredValueCents?: number | null;
  isFragile?: boolean;
  requiresCooling?: boolean;
}
export interface CurrentShipmentInput extends ShipmentCurrentExtras {
  deliveryNotes?: string | null;
  serviceCode?: string | null;
  packageCode?: string | null;
  addresses?: Array<{
    type: string;
    address?: CurrentPhysicalAddress | null;
    contactName?: string | null;
    contactCompany?: string | null;
    contactPhone?: string | null;
    contactEmail?: string | null;
    instructions?: string | null;
    propertyResidential?: boolean | null;
  }>;
  packages?: Array<
    PieceCurrentExtras & {
      id: string;
      index?: number | null;
      weightGrams?: number | null;
      lengthCm?: number | string | null;
      widthCm?: number | string | null;
      heightCm?: number | string | null;
      description?: string | null;
    }
  >;
}
const unknown = (): HistoryValue => ({
  availability: "UNKNOWN_LEGACY",
  value: null,
});
const known = (value: HistoryValue | undefined): HistoryValue =>
  value === undefined ? unknown() : value;
function addressValue(
  address: CurrentPhysicalAddress | null | undefined
): HistoryValue {
  if (!address) return { availability: "UNAVAILABLE", value: null };
  return {
    line1: known(address.line1),
    line2: known(address.line2),
    city: known(address.city),
    province: known(address.province),
    country: known(address.country),
    postalCode: known(address.postalCode),
    plusCode: known(address.plusCode),
    location: known(address.location),
  };
}
function planValue(
  plan: CurrentFulfillmentPlan | null | undefined
): HistoryValue {
  if (plan === undefined) return unknown();
  if (plan === null) return null;
  return {
    revision: plan.revision,
    inductionKind: plan.inductionKind,
    timeZone: plan.timeZone,
    pickupDate: plan.pickupDate,
    assumedHandoffAt: plan.assumedHandoffAt,
    readyAt: plan.readyAt,
    handoffBy: plan.handoffBy,
    atCarrierFastest: plan.atCarrierFastest,
    deliveredFastest: plan.deliveredFastest,
    deliveredSlowest: plan.deliveredSlowest,
    deliveredPrecision: plan.deliveredPrecision,
    acceptedAt: plan.acceptedAt,
    facilityDetailsVersion: plan.facilityDetailsVersion,
    dispatchAddressId: plan.dispatchAddressId,
    carrierDropoffAddressId: plan.carrierDropoffAddressId,
    dispatchCenterId: plan.dispatchCenterId,
    carrierDropoffLocationId: plan.carrierDropoffLocationId,
    dispatchAddress:
      !plan.dispatchCenterId && !plan.dispatchAddress
        ? { availability: "NOT_APPLICABLE", value: null }
        : addressValue(plan.dispatchAddress),
    carrierDropoffAddress:
      !plan.carrierDropoffLocationId && !plan.carrierDropoffAddress
        ? { availability: "NOT_APPLICABLE", value: null }
        : addressValue(plan.carrierDropoffAddress),
    facilityDisplay:
      plan.facilityDetailsVersion === 1
        ? {
            availability: "CAPTURED",
            value: {
              dispatchName: plan.dispatchName,
              dispatchCode: plan.dispatchCode,
              dispatchContactName: plan.dispatchContactName,
              dispatchContactPhone: plan.dispatchContactPhone,
              dispatchContactEmail: plan.dispatchContactEmail,
              carrierDropoffName: plan.carrierDropoffName,
            },
          }
        : unknown(),
    locationConfigVersion: plan.locationConfigVersion,
    dispatchProcessingMinutes: plan.dispatchProcessingMinutes,
    dispatchRunSchedule: plan.dispatchRunSchedule,
    carrierDropoffOperatingHours: plan.carrierDropoffOperatingHours,
    carrierDropoffTimeZone: plan.carrierDropoffTimeZone,
  };
}
export function currentShipmentValue(
  shipment: CurrentShipmentInput
): Record<string, HistoryValue> {
  return {
    parties:
      shipment.addresses?.map((party) => ({
        type: party.type,
        address: addressValue(party.address),
        contact: {
          name: known(party.contactName),
          company: known(party.contactCompany),
          phone: known(party.contactPhone),
          email: known(party.contactEmail),
        },
        instructions: known(party.instructions),
        residential: known(party.propertyResidential),
      })) ?? unknown(),
    deliveryNotes: known(shipment.deliveryNotes),
    packages:
      shipment.packages?.map((piece) => ({
        id: piece.id,
        index: known(piece.index),
        kind: known(piece.packageKind),
        weightGrams: known(piece.weightGrams),
        lengthCm: known(piece.lengthCm),
        widthCm: known(piece.widthCm),
        heightCm: known(piece.heightCm),
        weightOriginal: known(piece.weightOriginal),
        weightOriginalUnit: known(piece.weightOriginalUnit),
        lengthOriginal: known(piece.lengthOriginal),
        widthOriginal: known(piece.widthOriginal),
        heightOriginal: known(piece.heightOriginal),
        dimensionOriginalUnit: known(piece.dimensionOriginalUnit),
        description: known(piece.description),
        declaredValueCents: known(piece.declaredValueCents),
        isFragile: known(piece.isFragile),
        requiresCooling: known(piece.requiresCooling),
        packageTypeId: known(piece.packageTypeId),
        packageTypeDisplay: known(piece.packageTypeDisplay),
      })) ?? unknown(),
    carrierServiceId: known(shipment.carrierServiceId),
    serviceCode: known(shipment.serviceCode),
    packageCode: known(shipment.packageCode),
    selectedService: known(shipment.selectedService),
    options: known(shipment.options),
    fulfillmentPlan: planValue(shipment.fulfillmentPlan),
  };
}
